import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ChevronLeft, ChevronRight, Check, ArrowRight, Sun, Sunset, Moon, Camera, MapPin, User, Mail, Phone, Loader2, ExternalLink, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";

/* ------------------ Palette (scoped to this page) ------------------ */
const PALETTE = {
    blue: "#0055A4",       // Scotland flag deep blue
    blueDeep: "#0b1f3a",   // near-black navy for backgrounds
    blueSoft: "#1e6bb8",   // hover
    white: "#ffffff",
    mist: "#e7edf5",       // near-white subtle tint
    ink: "#0a1220",
};

const SCOTLAND_CENTRE = [56.8, -4.2];
const easeOut = [0.16, 1, 0.3, 1];

const SHOOT_TYPES = [
    { id: "wedding", label: "Wedding" },
    { id: "property", label: "Property" },
    { id: "estate", label: "Estate / Land" },
    { id: "event", label: "Event" },
    { id: "commercial", label: "Commercial" },
    { id: "landscape", label: "Landscape" },
    { id: "other", label: "Other" },
];

const TIME_SLOTS = [
    { id: "morning", label: "Morning", note: "07:00 – 11:00", Icon: Sun },
    { id: "afternoon", label: "Afternoon", note: "12:00 – 16:00", Icon: Sun },
    { id: "golden-hour", label: "Golden Hour", note: "Sunset ± 60m", Icon: Sunset },
    { id: "evening", label: "Evening", note: "After sunset", Icon: Moon },
];

/* Custom Leaflet marker in Scotland blue */
const pinIcon = new L.DivIcon({
    className: "book-map-pin",
    html: `<div style="width:22px;height:22px;border-radius:50%;background:${PALETTE.blue};border:3px solid ${PALETTE.white};box-shadow:0 6px 20px rgba(0,85,164,0.55), 0 0 0 6px rgba(0,85,164,0.15);"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
});

/* ------------------ Utilities ------------------ */
const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const sameYMD = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const humanDate = (d) =>
    d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

/* ------------------ Custom calendar ------------------ */
function Calendar({ value, onSelect, blockedSet, minDate = new Date() }) {
    const [cursor, setCursor] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });

    const monthLabel = cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    const firstDayIdx = (new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstDayIdx; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));

    const shift = (delta) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0);

    return (
        <div className="book-calendar" data-testid="book-calendar">
            <div className="flex items-center justify-between mb-4">
                <button
                    type="button"
                    onClick={() => shift(-1)}
                    className="p-2 border border-transparent hover:border-white/20 rounded-full transition"
                    aria-label="Previous month"
                    data-testid="calendar-prev"
                >
                    <ChevronLeft size={18} />
                </button>
                <div className="font-serif text-xl tracking-wide">{monthLabel}</div>
                <button
                    type="button"
                    onClick={() => shift(1)}
                    className="p-2 border border-transparent hover:border-white/20 rounded-full transition"
                    aria-label="Next month"
                    data-testid="calendar-next"
                >
                    <ChevronRight size={18} />
                </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
                {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                    <div key={i} className="text-center text-[10px] tracking-[0.28em] uppercase text-white/50 py-2">
                        {d}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
                {cells.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const iso = toISO(d);
                    const blocked = blockedSet.has(iso);
                    const isPast = d < min;
                    const disabled = blocked || isPast;
                    const selected = value && sameYMD(value, d);
                    const isToday = sameYMD(d, today);

                    let cls = "aspect-square text-sm relative flex items-center justify-center transition-all rounded-full ";
                    if (disabled) cls += "text-white/25 line-through cursor-not-allowed ";
                    else if (selected) cls += "bg-white text-[color:var(--book-blue)] font-semibold shadow-lg ";
                    else cls += "text-white hover:bg-white/15 cursor-pointer ";

                    return (
                        <button
                            key={i}
                            type="button"
                            disabled={disabled}
                            onClick={() => !disabled && onSelect(d)}
                            className={cls}
                            data-testid={`calendar-day-${iso}`}
                            data-blocked={blocked ? "true" : undefined}
                        >
                            {d.getDate()}
                            {isToday && !selected && (
                                <span
                                    aria-hidden
                                    className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                                    style={{ background: PALETTE.white }}
                                />
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="flex items-center gap-5 mt-6 text-[11px] tracking-wider uppercase text-white/60">
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-white" /> Available</span>
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-white/25" /> Booked / blocked</span>
            </div>
        </div>
    );
}

/* ------------------ Map picker ------------------ */
function ClickHandler({ onPick }) {
    useMapEvents({
        click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }),
    });
    return null;
}

function RecentreOnPin({ pin }) {
    const map = useMap();
    useEffect(() => {
        if (pin) map.panTo([pin.lat, pin.lng], { animate: true });
    }, [pin, map]);
    return null;
}

function MapPicker({ pin, onPick }) {
    return (
        <div
            className="relative overflow-hidden border border-white/15"
            style={{ height: "440px", background: PALETTE.blueDeep }}
            data-testid="book-map-picker"
        >
            <MapContainer
                center={SCOTLAND_CENTRE}
                zoom={6}
                minZoom={5}
                maxZoom={18}
                scrollWheelZoom
                style={{ height: "100%", width: "100%", background: PALETTE.blueDeep }}
            >
                <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    attribution='&copy; OpenStreetMap &copy; CARTO'
                    subdomains="abcd"
                    maxZoom={20}
                />
                <ClickHandler onPick={onPick} />
                {pin && <Marker position={[pin.lat, pin.lng]} icon={pinIcon} />}
                {pin && <RecentreOnPin pin={pin} />}
            </MapContainer>
            <div className="absolute top-3 left-3 z-[500] pointer-events-none bg-[color:var(--book-blue-deep)]/85 backdrop-blur px-3 py-2 text-[11px] tracking-[0.28em] uppercase text-white/85 border border-white/10">
                Click anywhere on the map to drop a pin
            </div>
            {pin && (
                <div className="absolute bottom-3 left-3 right-3 z-[500] bg-[color:var(--book-blue-deep)]/90 backdrop-blur px-4 py-3 border border-white/10 flex items-center gap-3" data-testid="book-map-pin-readout">
                    <MapPin size={16} className="text-white shrink-0" />
                    <div className="text-xs text-white/80">
                        <span className="tracking-[0.22em] uppercase text-white/60">Location</span>{" "}
                        <span className="font-mono tabular-nums">{pin.lat.toFixed(4)}°, {pin.lng.toFixed(4)}°</span>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ------------------ Section reveal ------------------ */
function Section({ children, delay = 0, className = "", id }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-10% 0px" });
    return (
        <motion.div
            id={id}
            ref={ref}
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay, ease: easeOut }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

/* ------------------ Page ------------------ */
export default function Book() {
    usePageMeta({
        title: "Book a Shoot — DTScotslad",
        description: "Schedule your own aerial shoot in Scotland — pick a date, a location on the map, and a time. Real-time availability from Mission Control.",
    });

    const [availability, setAvailability] = useState({ blocked_dates: [], open_next_60_days: null });
    const [loadingAvail, setLoadingAvail] = useState(true);

    const [selectedDate, setSelectedDate] = useState(null);
    const [alternateDate, setAlternateDate] = useState(null);
    const [timeSlot, setTimeSlot] = useState("");
    const [pin, setPin] = useState(null);
    const [form, setForm] = useState({
        client_name: "",
        email: "",
        phone: "",
        shoot_type: "",
        location_name: "",
        address: "",
        notes: "",
    });
    const [submitState, setSubmitState] = useState("idle"); // idle | loading | success | error
    const [errorMsg, setErrorMsg] = useState("");
    const [reference, setReference] = useState("");

    const blockedSet = useMemo(() => new Set(availability.blocked_dates || []), [availability]);

    const loadAvailability = () => {
        setLoadingAvail(true);
        return api.get("/bookings/availability")
            .then((r) => setAvailability(r.data))
            .finally(() => setLoadingAvail(false));
    };

    useEffect(() => { loadAvailability(); }, []);

    // Support deep-linking to the calendar via #book-form-step-1 from the
    // header's "Calendar" dropdown entry. React Router doesn't scroll to hash
    // targets by default; we do it here after mount + a short delay for the
    // motion reveal to have laid out the section.
    const location = useLocation();
    useEffect(() => {
        if (typeof window === "undefined") return;
        const hash = location.hash?.slice(1);
        if (!hash) return;
        const scroll = () => {
            const el = document.getElementById(hash);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        };
        // Two ticks — one for React to mount the target, one for framer-motion
        // to finish its initial layout so scrollIntoView lands accurately.
        const t = setTimeout(scroll, 250);
        return () => clearTimeout(t);
    }, [location.hash]);

    const canSubmit =
        !!selectedDate &&
        !!timeSlot &&
        !!pin &&
        form.client_name.trim().length > 1 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
        !!form.shoot_type;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) {
            setErrorMsg("Please complete all required fields, including date, time and location.");
            setSubmitState("error");
            return;
        }
        setSubmitState("loading");
        setErrorMsg("");
        try {
            const payload = {
                client_name: form.client_name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim(),
                shoot_type: form.shoot_type,
                preferred_date: toISO(selectedDate),
                alternate_date: alternateDate ? toISO(alternateDate) : "",
                time_slot: timeSlot,
                location_name: form.location_name.trim(),
                address: form.address.trim(),
                lat: pin.lat,
                lng: pin.lng,
                notes: form.notes.trim(),
            };
            const { data } = await api.post("/bookings", payload);
            setReference(data.id);
            setSubmitState("success");
            loadAvailability(); // refresh so the newly-booked date shows as blocked
        } catch (err) {
            setErrorMsg(err.response?.data?.detail || "Something went wrong. Please try again.");
            setSubmitState("error");
        }
    };

    /* Success state */
    if (submitState === "success") {
        return (
            <div data-testid="book-page" className="min-h-[80vh] flex items-center" style={{ background: PALETTE.blueDeep, color: PALETTE.white }}>
                <div className="max-w-3xl mx-auto px-6 lg:px-12 py-24 text-center" data-testid="book-success">
                    <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-8" style={{ background: PALETTE.blue }}>
                        <Check size={36} />
                    </div>
                    <div className="overline mb-4" style={{ color: "rgba(255,255,255,0.7)" }}>Request received</div>
                    <h1 className="font-serif tracking-tight leading-[0.98] mb-6" style={{ fontSize: "clamp(2.4rem, 5.5vw, 4.4rem)" }}>
                        The flight is<br /><span className="italic" style={{ color: PALETTE.mist }}>on the schedule.</span>
                    </h1>
                    <p className="text-lg leading-relaxed opacity-80 max-w-xl mx-auto mb-8">
                        A confirmation will arrive shortly. I&rsquo;ll be in touch within 48 hours to confirm the weather window and finalise the shoot brief.
                    </p>
                    <div className="inline-flex items-center gap-3 border border-white/20 px-4 py-2 text-xs tracking-[0.28em] uppercase mb-10" data-testid="book-reference">
                        Reference · {reference}
                    </div>
                    <div className="flex gap-3 justify-center flex-wrap">
                        <a
                            href="/"
                            className="px-5 py-3 text-xs uppercase tracking-[0.28em]"
                            style={{ background: PALETTE.white, color: PALETTE.blue }}
                        >
                            Return home
                        </a>
                        <a
                            href="/gallery"
                            className="px-5 py-3 text-xs uppercase tracking-[0.28em] border border-white/30 hover:border-white transition-colors"
                        >
                            See past flights
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="book-page"
            style={{
                background: PALETTE.blueDeep,
                color: PALETTE.white,
                // Scoped CSS variables so nested classes can reference the palette
                "--book-blue": PALETTE.blue,
                "--book-blue-deep": PALETTE.blueDeep,
                "--book-blue-soft": PALETTE.blueSoft,
                "--book-white": PALETTE.white,
                "--book-mist": PALETTE.mist,
            }}
        >
            {/* Saltire-inspired hero */}
            <section className="relative overflow-hidden" data-testid="book-hero">
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.18]"
                    style={{
                        backgroundImage: `linear-gradient(45deg, transparent 46%, ${PALETTE.white} 47%, ${PALETTE.white} 53%, transparent 54%), linear-gradient(-45deg, transparent 46%, ${PALETTE.white} 47%, ${PALETTE.white} 53%, transparent 54%)`,
                        backgroundSize: "180px 180px",
                    }}
                />
                <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                        background: `radial-gradient(60% 50% at 50% 30%, rgba(30,107,184,0.35) 0%, rgba(0,0,0,0) 65%)`,
                    }}
                />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24">
                    <div className="overline mb-4" style={{ color: "rgba(255,255,255,0.65)" }}>
                        <span className="inline-flex items-center gap-3">
                            <span className="w-8 h-px" style={{ background: PALETTE.white }} />
                            Book · Charter · Fly
                        </span>
                    </div>
                    <h1
                        className="font-serif font-light tracking-tighter mb-6"
                        style={{ fontSize: "clamp(2.6rem, 7.5vw, 7rem)", lineHeight: 0.94 }}
                        data-testid="book-hero-title"
                    >
                        Schedule your<br />
                        <span className="italic" style={{ color: PALETTE.mist }}>own flight.</span>
                    </h1>
                    <p className="text-lg lg:text-xl leading-relaxed opacity-80 max-w-2xl mb-10">
                        Pick a date, a location on the map, and a time. Availability updates in real time — if you can see the day, it&rsquo;s open.
                    </p>
                    <div className="flex flex-wrap items-center gap-6" data-testid="book-availability-badge">
                        <div className="border border-white/20 px-4 py-3 backdrop-blur-md bg-white/5">
                            <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">Availability</div>
                            <div className="mt-1 font-serif text-2xl tabular-nums">
                                24/7 available time
                            </div>
                        </div>
                        <div className="text-sm opacity-70 max-w-xs">
                            Every open day in the calendar below is currently unbooked. Popular Highland golden-hour slots go first.
                        </div>
                        <a
                            href="/safety"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-white/80 hover:text-white transition-colors border-b border-white/30 hover:border-white pb-1"
                            data-testid="book-safety-link"
                        >
                            CAA-compliant · insured — Read the Pilot&rsquo;s Code
                            <ExternalLink size={12} />
                        </a>
                    </div>
                </div>
            </section>

            {/* -------- Flight Packages ---------------------------------- */}
            <FlightPackages />

            {/* Booking flow */}
            <form onSubmit={submit} className="max-w-7xl mx-auto px-6 lg:px-12 pb-24 space-y-16" data-testid="book-form">
                {/* STEP 1 — DATE */}
                <Section id="book-form-step-1">
                    <StepHeader n="01" title="Pick a date" subtitle="Booked dates are struck through. Choose an alternate if you're flexible." />
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
                        <div className="lg:col-span-6 border border-white/15 bg-white/[0.03] p-6 lg:p-8">
                            <Calendar value={selectedDate} onSelect={setSelectedDate} blockedSet={blockedSet} />
                        </div>
                        <div className="lg:col-span-6 space-y-6">
                            <div className="border border-white/15 p-6" data-testid="book-selected-date">
                                <div className="overline mb-2" style={{ color: "rgba(255,255,255,0.6)" }}>Preferred date</div>
                                {selectedDate ? (
                                    <div>
                                        <div className="font-serif text-2xl">{humanDate(selectedDate)}</div>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedDate(null)}
                                            className="mt-3 text-xs tracking-[0.22em] uppercase text-white/60 hover:text-white transition-colors"
                                        >
                                            Change
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-white/50">Not yet selected.</div>
                                )}
                            </div>
                            <div className="border border-white/15 p-6" data-testid="book-alternate-date">
                                <div className="overline mb-2" style={{ color: "rgba(255,255,255,0.6)" }}>Alternate (optional)</div>
                                {alternateDate ? (
                                    <div>
                                        <div className="font-serif text-2xl">{humanDate(alternateDate)}</div>
                                        <button
                                            type="button"
                                            onClick={() => setAlternateDate(null)}
                                            className="mt-3 text-xs tracking-[0.22em] uppercase text-white/60 hover:text-white transition-colors"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-white/50 text-sm">
                                        Leave empty, or use the button below to move the current selection into this slot and pick a new preferred date.
                                    </div>
                                )}
                                {/* Simpler UX: an inline button set to pick alternate */}
                                <div className="mt-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (selectedDate) {
                                                setAlternateDate(selectedDate);
                                                setSelectedDate(null);
                                            }
                                        }}
                                        disabled={!selectedDate}
                                        className="text-xs tracking-[0.22em] uppercase border border-white/20 hover:border-white px-3 py-2 disabled:opacity-30 transition-colors"
                                    >
                                        Move current selection to alternate
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>

                {/* STEP 2 — TIME */}
                <Section>
                    <StepHeader n="02" title="Pick a time" subtitle="Golden hour is the crown jewel — but every slot has its own light." />
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="book-time-slots">
                        {TIME_SLOTS.map((s) => {
                            const active = timeSlot === s.id;
                            return (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => setTimeSlot(s.id)}
                                    data-testid={`time-slot-${s.id}`}
                                    className="text-left p-6 border transition-all relative"
                                    style={{
                                        borderColor: active ? PALETTE.white : "rgba(255,255,255,0.15)",
                                        background: active ? PALETTE.white : "transparent",
                                        color: active ? PALETTE.blue : PALETTE.white,
                                    }}
                                >
                                    <s.Icon size={20} className="mb-4" />
                                    <div className="font-serif text-xl">{s.label}</div>
                                    <div className="text-[11px] tracking-[0.22em] uppercase mt-1 opacity-80">{s.note}</div>
                                </button>
                            );
                        })}
                    </div>
                </Section>

                {/* STEP 3 — MAP */}
                <Section>
                    <StepHeader n="03" title="Pick a location" subtitle="Click anywhere on Scotland to drop a pin. You can adjust it any time." />
                    <MapPicker pin={pin} onPick={setPin} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <Input
                            label="Location name (e.g., Balvaird Castle)"
                            value={form.location_name}
                            onChange={(v) => setForm({ ...form, location_name: v })}
                            testid="book-input-location-name"
                        />
                        <Input
                            label="Nearest address / postcode"
                            value={form.address}
                            onChange={(v) => setForm({ ...form, address: v })}
                            testid="book-input-address"
                        />
                    </div>
                </Section>

                {/* STEP 4 — DETAILS */}
                <Section>
                    <StepHeader n="04" title="Your details" subtitle="I'll respond within 48 hours to confirm the weather window." />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Full name" required Icon={User} value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} testid="book-input-name" />
                        <Input label="Email" type="email" required Icon={Mail} value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="book-input-email" />
                        <Input label="Phone (optional)" type="tel" Icon={Phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="book-input-phone" />
                        <div>
                            <label className="block text-[11px] tracking-[0.28em] uppercase text-white/60 mb-2">
                                <Camera size={12} className="inline mr-2 -mt-0.5" />
                                Type of shoot
                            </label>
                            <select
                                value={form.shoot_type}
                                onChange={(e) => setForm({ ...form, shoot_type: e.target.value })}
                                className="w-full bg-transparent border-b border-white/25 focus:border-white outline-none py-2 text-white"
                                data-testid="book-select-shoot-type"
                                required
                            >
                                <option value="" style={{ background: PALETTE.blueDeep }}>— Choose —</option>
                                {SHOOT_TYPES.map((t) => (
                                    <option key={t.id} value={t.id} style={{ background: PALETTE.blueDeep }}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-[11px] tracking-[0.28em] uppercase text-white/60 mb-2">Notes / brief (optional)</label>
                            <textarea
                                rows={4}
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                className="w-full bg-transparent border border-white/20 focus:border-white outline-none p-3 text-white text-sm resize-none"
                                placeholder="Anything I should know? Access notes, coverage priorities, deliverable format…"
                                data-testid="book-input-notes"
                            />
                        </div>
                    </div>
                </Section>

                {/* Submit */}
                <Section>
                    <div className="border-t border-white/15 pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                        <div className="text-sm text-white/70">
                            By requesting a shoot you agree to my terms and a follow-up conversation to lock the brief.
                        </div>
                        <button
                            type="submit"
                            disabled={!canSubmit || submitState === "loading"}
                            className="inline-flex items-center justify-center gap-3 px-8 py-4 text-sm uppercase tracking-[0.28em] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                            style={{ background: PALETTE.white, color: PALETTE.blue }}
                            data-testid="book-submit"
                        >
                            {submitState === "loading" ? (<><Loader2 size={16} className="animate-spin" /> Sending</>) : (<>Request the flight <ArrowRight size={16} /></>)}
                        </button>
                    </div>
                    {submitState === "error" && errorMsg && (
                        <p className="mt-4 text-sm text-red-300" data-testid="book-error">{errorMsg}</p>
                    )}
                </Section>
            </form>
        </div>
    );
}

/* --- little sub-components --- */
function StepHeader({ n, title, subtitle }) {
    return (
        <div className="mb-8 flex items-baseline gap-6 flex-wrap">
            <span className="font-serif italic text-6xl opacity-25" style={{ color: PALETTE.white }}>{n}</span>
            <div>
                <h2 className="font-serif tracking-tight leading-tight" style={{ fontSize: "clamp(1.6rem, 3vw, 2.6rem)" }}>{title}</h2>
                {subtitle && <p className="text-sm text-white/60 mt-1">{subtitle}</p>}
            </div>
        </div>
    );
}

function Input({ label, value, onChange, testid, type = "text", Icon, required }) {
    return (
        <div>
            <label className="block text-[11px] tracking-[0.28em] uppercase text-white/60 mb-2">
                {Icon && <Icon size={12} className="inline mr-2 -mt-0.5" />}
                {label} {required && <span className="text-white/40">·</span>}
            </label>
            <input
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-transparent border-b border-white/25 focus:border-white outline-none py-2 text-white placeholder-white/30"
                data-testid={testid}
            />
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Flight Packages — official Drone Vista Scotland service tiers.             */
/* Sits between the hero and the booking form so pricing is unmissable        */
/* before visitors dive into date/time selection.                             */
/* -------------------------------------------------------------------------- */
const PACKAGES = [
    {
        id: "standard",
        badge: "01",
        name: "The Standard Flight",
        duration: "5 minutes",
        capture: "Raw 4K aerial capture",
        ideal: "Small properties, single landmarks, high-impact social-media reveals.",
        includes: [
            "Single-lens 4K wide capture",
            "Full CAA pre-flight safety assessment",
            "Raw-file delivery within 7 days",
        ],
        price: 85,
        priceNote: "+ Travel",
        featured: false,
    },
    {
        id: "premium",
        badge: "02",
        name: "The Premium Flight",
        duration: "10 minutes",
        capture: "Raw 4K aerial capture · dual-lens",
        ideal: "Larger estates, business promotions, detailed cinematic orbits.",
        includes: [
            "Dual-lens 4K (Wide + 70mm Telephoto for cinematic compression)",
            "CAA pre-flight safety assessment & risk report",
            "Raw-file delivery within 7 days",
        ],
        price: 145,
        priceNote: "+ Travel",
        featured: true,
    },
    {
        id: "top-tier",
        badge: "03",
        name: "The Top-Tier Flight",
        duration: "15 minutes",
        capture: "Raw 4K aerial capture · full 'Beast' rig",
        ideal: "Heritage sites, commercial marketing, complex cinematic sequences.",
        includes: [
            "Multi-angle 4K coverage & sweeping landscape reveals",
            "Dual-lens capture with expanded shot list",
            "CAA pre-flight safety assessment & risk report",
            "Raw-file delivery within 7 days",
        ],
        price: 225,
        priceNote: "+ Travel",
        featured: false,
    },
];

function FlightPackages() {
    return (
        <section
            className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 relative"
            data-testid="flight-packages"
        >
            {/* -- Header -- */}
            <header className="mb-14 max-w-3xl">
                <div className="overline mb-4" style={{ color: "#0055A4", fontWeight: 700 }}>
                    <span className="editorial-rule" style={{ background: "#0055A4" }} />
                    Flight Packages
                </div>
                <h2
                    className="font-serif font-light tracking-tight leading-[0.95]"
                    style={{ fontSize: "clamp(2.2rem, 5vw, 4.6rem)" }}
                    data-testid="flight-packages-title"
                >
                    Cinematic Aerial Services<br/>
                    <span className="italic" style={{ color: "#0055A4" }}>by DTScotslad.</span>
                </h2>
                <p className="text-[var(--sb-text-dim)] mt-6 text-base lg:text-lg leading-relaxed max-w-2xl">
                    Professional 4K Drone Capture · CAA Licensed · Fully Insured.
                </p>
            </header>

            {/* -- Package cards -- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="flight-packages-grid">
                {PACKAGES.map((p) => (
                    <article
                        key={p.id}
                        className={`relative flex flex-col p-6 md:p-8 lg:p-10 border transition-colors ${
                            p.featured
                                ? "border-[#0055A4]"
                                : "border-[var(--sb-border)] hover:border-[var(--sb-brand)]/60"
                        }`}
                        style={p.featured ? { background: "rgba(0, 85, 164, 0.06)" } : undefined}
                        data-testid={`flight-package-${p.id}`}
                    >
                        {p.featured && (
                            <span
                                className="absolute top-0 right-0 text-[10px] tracking-[0.28em] uppercase px-3 py-1 text-white"
                                style={{ background: "#0055A4" }}
                                data-testid="flight-package-featured-badge"
                            >
                                Most requested
                            </span>
                        )}
                        <div className="flex items-baseline justify-between mb-4">
                            <span className="font-mono text-xs tracking-[0.28em] text-[var(--sb-text-muted)]">{p.badge}</span>
                            <span className="text-xs tracking-[0.22em] uppercase text-[var(--sb-text-muted)]">{p.duration}</span>
                        </div>
                        <h3 className="font-serif text-2xl lg:text-3xl tracking-tight leading-tight mb-2">
                            {p.name}
                        </h3>
                        <p className="text-sm text-[var(--sb-brand)] mb-5 italic">{p.capture}</p>
                        <p className="text-sm text-[var(--sb-text-dim)] leading-relaxed mb-6">
                            <span className="text-[var(--sb-text-muted)] uppercase tracking-[0.2em] text-[10px] block mb-1">Ideal for</span>
                            {p.ideal}
                        </p>
                        <ul className="space-y-2.5 mb-8 flex-1">
                            {p.includes.map((line, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm text-[var(--sb-text-dim)] leading-relaxed">
                                    <Check size={14} className="mt-1 shrink-0" style={{ color: "#0055A4" }} />
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-auto pt-6 border-t border-[var(--sb-border)]">
                            <div className="flex items-baseline gap-2 mb-5">
                                <span className="font-serif text-4xl tracking-tight" style={{ color: p.featured ? "#0055A4" : "var(--sb-text)" }}>
                                    £{p.price.toFixed(2)}
                                </span>
                                <span className="text-xs uppercase tracking-[0.22em] text-[var(--sb-text-muted)]">{p.priceNote}</span>
                            </div>
                            <a
                                href="#book-form-step-1"
                                onClick={(e) => {
                                    // Smooth-scroll to the booking form step 1 anchor
                                    e.preventDefault();
                                    document.getElementById("book-form-step-1")?.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                                className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] pb-2 border-b transition-colors ${
                                    p.featured
                                        ? "text-[#0055A4] border-[#0055A4] hover:text-[var(--sb-text)] hover:border-[var(--sb-text)]"
                                        : "text-[var(--sb-brand)] border-[var(--sb-brand)]/40 hover:text-[var(--sb-text)] hover:border-[var(--sb-text)]"
                                }`}
                                data-testid={`flight-package-book-${p.id}`}
                            >
                                Book this flight <ArrowRight size={12} />
                            </a>
                        </div>
                    </article>
                ))}
            </div>

            {/* -- Satisfaction Promise ---------------------------------------
                 A single reassurance badge under the three tier cards. Sets
                 us apart from competitors who charge for every minor tweak.
            --------------------------------------------------------------- */}
            <div className="mt-10 flex justify-center" data-testid="satisfaction-promise">
                <div
                    className="relative w-full max-w-3xl border border-[color:#0055A4]/40 px-6 py-5 md:px-8 md:py-6"
                    style={{
                        background: "linear-gradient(135deg, rgba(0,85,164,0.10) 0%, rgba(20,42,77,0.35) 100%)",
                    }}
                >
                    {/* Corner accents — a subtle "certificate" feel */}
                    <span aria-hidden className="absolute top-0 left-0 w-3 h-3 border-t border-l" style={{ borderColor: "#0055A4" }} />
                    <span aria-hidden className="absolute top-0 right-0 w-3 h-3 border-t border-r" style={{ borderColor: "#0055A4" }} />
                    <span aria-hidden className="absolute bottom-0 left-0 w-3 h-3 border-b border-l" style={{ borderColor: "#0055A4" }} />
                    <span aria-hidden className="absolute bottom-0 right-0 w-3 h-3 border-b border-r" style={{ borderColor: "#0055A4" }} />

                    <div className="flex items-start gap-4 md:gap-5">
                        <div
                            className="shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center"
                            style={{
                                background: "rgba(0,85,164,0.15)",
                                border: "1.5px solid #0055A4",
                            }}
                            data-testid="satisfaction-promise-icon"
                        >
                            <ShieldCheck size={26} style={{ color: "#5A9BD8" }} strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div
                                className="overline mb-1.5"
                                style={{ color: "#5A9BD8", fontWeight: 700, letterSpacing: "0.28em" }}
                            >
                                The DTScotslad Satisfaction Promise
                            </div>
                            <p
                                className="font-serif text-lg md:text-xl leading-snug mb-2"
                                style={{ color: "var(--sb-text)" }}
                                data-testid="satisfaction-promise-title"
                            >
                                Every frame reviewed with you &mdash; until it&rsquo;s right.
                            </p>
                            <p className="text-sm text-[var(--sb-text-dim)] leading-relaxed">
                                Minor tweaks, re-colours, and cut adjustments are always included &mdash; never billed extra.
                                If the final delivery doesn&rsquo;t sing, we keep flying until it does.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* -- Additional terms & logistics -- */}
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="flight-packages-terms">
                <div className="border border-[var(--sb-border)] p-6">
                    <div className="overline mb-3" style={{ color: "#0055A4", fontWeight: 700 }}>Standby rate</div>
                    <p className="font-serif text-2xl mb-2">£40.00 <span className="text-sm text-[var(--sb-text-muted)] uppercase tracking-[0.22em]">/ hr</span></p>
                    <p className="text-sm text-[var(--sb-text-dim)] leading-relaxed">Applicable for on-site weather delays or event standby.</p>
                </div>
                <div className="border border-[var(--sb-border)] p-6">
                    <div className="overline mb-3" style={{ color: "#0055A4", fontWeight: 700 }}>Travel &amp; fuel</div>
                    <p className="font-serif text-2xl mb-2">First 10 miles <span className="text-sm text-[var(--sb-text-muted)] uppercase tracking-[0.22em]">included</span></p>
                    <p className="text-sm text-[var(--sb-text-dim)] leading-relaxed">£0.45 per mile thereafter from Perth base.</p>
                </div>
                <div className="border border-[var(--sb-border)] p-6">
                    <div className="overline mb-3" style={{ color: "#0055A4", fontWeight: 700 }}>Safety first</div>
                    <p className="font-serif text-2xl mb-2">Every flight</p>
                    <p className="text-sm text-[var(--sb-text-dim)] leading-relaxed">Includes a full CAA pre-flight safety assessment &amp; risk report.</p>
                </div>
            </div>

            {/* -- FAQ + Satisfaction Guarantee ------------------------------- */}
            <BookingFAQ />

            {/* -- Pricing Quiz + Calculator --------------------------------- */}
            <FlightFinder />
        </section>
    );
}


/* -------------------------------------------------------------------------- */
/* FlightFinder — 3-question quiz + travel-distance calculator                */
/*                                                                            */
/* Segments visitors toward Standard / Premium / Top-Tier based on scope,     */
/* cinematic requirements, and required flight time, then estimates the       */
/* total with a live travel-cost calculator (£0.45 per mile after the first   */
/* 10 free miles from the Perth base).                                        */
/*                                                                            */
/* Nudge: if scope or cinematic answers point at Premium/Top-Tier but flight  */
/* time is only 5 mins (Standard), we prompt the visitor to reconsider —      */
/* since the premium features they've asked for are only fully unlocked on    */
/* the longer packages.                                                       */
/* -------------------------------------------------------------------------- */

const FINDER_QUESTIONS = [
    {
        id: "scope",
        prompt: "What is the primary subject of your shoot?",
        options: [
            { key: "A", label: "A single landmark or simple landscape reveal.", tier: "standard" },
            { key: "B", label: "A large estate, business property, or commercial promotion.", tier: "premium" },
            { key: "C", label: "A heritage site, complex infrastructure, or high-end cinematic production.", tier: "top-tier" },
        ],
    },
    {
        id: "detail",
        prompt: "What level of cinematic detail do you require?",
        options: [
            { key: "A", label: "Standard wide-angle coverage.", tier: "standard" },
            { key: "B", label: "Cinematic orbits and long-range telephoto shots (70mm).", tier: "premium" },
            { key: "C", label: "Full-scale production with advanced camera movements and complex sequences.", tier: "top-tier" },
        ],
    },
    {
        id: "flightTime",
        prompt: "How much total flight time do you estimate is needed to capture all your required angles?",
        options: [
            { key: "A", label: "Up to 5 minutes.", tier: "standard" },
            { key: "B", label: "Up to 10 minutes.", tier: "premium" },
            { key: "C", label: "15 minutes or more.", tier: "top-tier" },
        ],
    },
];

const TIER_ORDER = { "standard": 1, "premium": 2, "top-tier": 3 };
const FREE_MILES = 10;
const PER_MILE = 0.45;

function pickRecommendation(answers) {
    // Highest-tier answer wins. Standard < Premium < Top-Tier.
    // Ties resolve to the higher-ranked tier (mirrors "when in doubt,
    // give them the bigger package that fits every answered need").
    const values = Object.values(answers).filter(Boolean);
    if (values.length === 0) return null;
    let best = "standard";
    for (const t of values) {
        if (TIER_ORDER[t] > TIER_ORDER[best]) best = t;
    }
    return best;
}

function nudgeMessage(answers) {
    // If any premium/top-tier feature is selected AND flight time answer is
    // "5 minutes" (Standard), gently upsell — the premium features aren't
    // unlocked at the Standard 5-min tier.
    if (!answers.flightTime || answers.flightTime !== "standard") return null;
    const wantsAbove = [answers.scope, answers.detail].some(
        (t) => t && TIER_ORDER[t] > TIER_ORDER["standard"]
    );
    if (!wantsAbove) return null;
    return "You've asked for cinematic features that live on the Premium rig. The Standard flight caps at 5 minutes — Premium doubles your air-time so those features actually get used.";
}

function OptionButton({ selected, label, letter, onClick, tone, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full text-left flex items-start gap-3 p-4 border transition-all group"
            style={{
                borderColor: selected ? tone : "var(--sb-border)",
                background: selected
                    ? "linear-gradient(135deg, rgba(0,85,164,0.14), rgba(20,42,77,0.4))"
                    : "transparent",
            }}
            data-testid={testid}
            aria-pressed={selected}
        >
            <span
                className="shrink-0 w-7 h-7 flex items-center justify-center text-[11px] font-serif tracking-wider"
                style={{
                    border: `1px solid ${selected ? tone : "var(--sb-border)"}`,
                    color: selected ? tone : "var(--sb-text-muted)",
                    background: selected ? "rgba(0,85,164,0.08)" : "transparent",
                }}
            >
                {letter}
            </span>
            <span
                className="text-sm md:text-base leading-relaxed"
                style={{ color: selected ? "var(--sb-text)" : "var(--sb-text-dim)" }}
            >
                {label}
            </span>
        </button>
    );
}

function FlightFinder() {
    const [answers, setAnswers] = useState({ scope: null, detail: null, flightTime: null });
    const [miles, setMiles] = useState("");

    const recommendedId = useMemo(() => pickRecommendation(answers), [answers]);
    const recommendation = useMemo(
        () => (recommendedId ? PACKAGES.find((p) => p.id === recommendedId) : null),
        [recommendedId]
    );
    const nudge = useMemo(() => nudgeMessage(answers), [answers]);

    const milesNum = Math.max(0, parseFloat(miles) || 0);
    const chargeableMiles = Math.max(0, milesNum - FREE_MILES);
    const travelCost = chargeableMiles * PER_MILE;
    const total = recommendation ? recommendation.price + travelCost : null;

    const answered = Object.values(answers).filter(Boolean).length;
    const canBookScroll = () => {
        const el = document.getElementById("book-form-step-1");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    function reset() {
        setAnswers({ scope: null, detail: null, flightTime: null });
        setMiles("");
    }

    return (
        <div className="mt-20" data-testid="flight-finder">
            <div className="text-center mb-10">
                <div className="overline mb-3" style={{ color: "#5A9BD8", fontWeight: 700 }}>
                    Not sure which package?
                </div>
                <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight" style={{ color: "var(--sb-text)" }}>
                    Answer three questions.
                </h3>
                <p className="text-sm md:text-base text-[var(--sb-text-dim)] mt-4 max-w-xl mx-auto leading-relaxed">
                    A quick needs-assessment. I&rsquo;ll recommend the right flight for your project and give you an
                    estimated total with travel included.
                </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-10">
                {FINDER_QUESTIONS.map((q, qi) => (
                    <div key={q.id} data-testid={`flight-finder-q-${q.id}`}>
                        <div className="flex items-baseline gap-3 mb-4">
                            <span
                                className="font-serif text-2xl"
                                style={{ color: "#5A9BD8" }}
                            >
                                0{qi + 1}
                            </span>
                            <h4 className="font-serif text-lg md:text-xl leading-snug" style={{ color: "var(--sb-text)" }}>
                                {q.prompt}
                            </h4>
                        </div>
                        <div className="grid grid-cols-1 gap-2 md:gap-3">
                            {q.options.map((opt) => (
                                <OptionButton
                                    key={opt.key}
                                    selected={answers[q.id] === opt.tier}
                                    label={opt.label}
                                    letter={opt.key}
                                    tone="#5A9BD8"
                                    onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt.tier }))}
                                    testid={`flight-finder-opt-${q.id}-${opt.key}`}
                                />
                            ))}
                        </div>
                    </div>
                ))}

                {/* Travel distance */}
                <div data-testid="flight-finder-q-travel">
                    <div className="flex items-baseline gap-3 mb-4">
                        <span className="font-serif text-2xl" style={{ color: "#5A9BD8" }}>04</span>
                        <h4 className="font-serif text-lg md:text-xl leading-snug" style={{ color: "var(--sb-text)" }}>
                            How far from my Perth base? <span className="text-[var(--sb-text-muted)] font-sans text-sm">(optional)</span>
                        </h4>
                    </div>
                    <div className="flex items-center gap-3 border border-[var(--sb-border)] px-4 py-3" style={{ maxWidth: 320 }}>
                        <input
                            type="number"
                            min="0"
                            step="1"
                            inputMode="numeric"
                            placeholder="Distance in miles"
                            value={miles}
                            onChange={(e) => setMiles(e.target.value)}
                            className="flex-1 bg-transparent outline-none text-[var(--sb-text)] placeholder:text-[var(--sb-text-muted)]"
                            data-testid="flight-finder-miles"
                            aria-label="Distance in miles from Perth base"
                        />
                        <span className="text-xs uppercase tracking-[0.22em] text-[var(--sb-text-muted)]">miles</span>
                    </div>
                    <p className="text-xs text-[var(--sb-text-muted)] mt-2">
                        First {FREE_MILES} miles included. £{PER_MILE.toFixed(2)} per mile after that.
                    </p>
                </div>

                {/* Upsell nudge */}
                {nudge && (
                    <div
                        className="border p-4 md:p-5 flex items-start gap-3"
                        style={{ borderColor: "rgba(255,180,50,0.4)", background: "rgba(255,180,50,0.05)" }}
                        data-testid="flight-finder-nudge"
                    >
                        <div className="shrink-0 mt-0.5 text-amber-300">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                                <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            </svg>
                        </div>
                        <p className="text-sm text-[var(--sb-text-dim)] leading-relaxed">{nudge}</p>
                    </div>
                )}

                {/* Recommendation card */}
                {recommendation && answered === FINDER_QUESTIONS.length && (
                    <div
                        className="relative border p-6 md:p-8"
                        style={{
                            borderColor: "rgba(107,167,222,0.5)",
                            background: "linear-gradient(135deg, rgba(0,85,164,0.16) 0%, rgba(20,42,77,0.55) 100%)",
                        }}
                        data-testid="flight-finder-recommendation"
                    >
                        <span aria-hidden className="absolute top-0 left-0 w-4 h-4 border-t border-l" style={{ borderColor: "#0055A4" }} />
                        <span aria-hidden className="absolute top-0 right-0 w-4 h-4 border-t border-r" style={{ borderColor: "#0055A4" }} />
                        <span aria-hidden className="absolute bottom-0 left-0 w-4 h-4 border-b border-l" style={{ borderColor: "#0055A4" }} />
                        <span aria-hidden className="absolute bottom-0 right-0 w-4 h-4 border-b border-r" style={{ borderColor: "#0055A4" }} />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                            <div>
                                <div className="overline mb-2" style={{ color: "#5A9BD8", fontWeight: 700 }}>
                                    Recommended for you
                                </div>
                                <div
                                    className="font-serif text-3xl md:text-4xl leading-tight mb-2"
                                    style={{ color: "var(--sb-text)" }}
                                    data-testid="flight-finder-tier-name"
                                >
                                    {recommendation.name}
                                </div>
                                <p className="text-sm text-[var(--sb-text-dim)] leading-relaxed">
                                    {recommendation.ideal}
                                </p>
                            </div>
                            <div className="md:text-right">
                                <div className="text-xs uppercase tracking-[0.28em] text-[var(--sb-text-muted)] mb-1">
                                    Estimated total
                                </div>
                                <div
                                    className="font-serif text-4xl md:text-6xl leading-none mb-3"
                                    style={{ color: "var(--sb-text)" }}
                                    data-testid="flight-finder-total"
                                >
                                    £{total.toFixed(2)}
                                </div>
                                <div className="text-xs text-[var(--sb-text-muted)] leading-relaxed space-y-0.5">
                                    <div>Package £{recommendation.price.toFixed(2)}</div>
                                    {chargeableMiles > 0 && (
                                        <div data-testid="flight-finder-travel-line">
                                            Travel {chargeableMiles.toFixed(0)} mi × £{PER_MILE.toFixed(2)} = £{travelCost.toFixed(2)}
                                        </div>
                                    )}
                                    {milesNum > 0 && chargeableMiles === 0 && (
                                        <div>Travel: within {FREE_MILES}-mile free zone</div>
                                    )}
                                    {milesNum === 0 && (
                                        <div>Add distance above for a travel-inclusive quote.</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <button
                                type="button"
                                onClick={canBookScroll}
                                className="btn-primary inline-flex items-center gap-2"
                                data-testid="flight-finder-book"
                            >
                                Book this flight <ArrowRight size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={reset}
                                className="btn-outline text-xs"
                                data-testid="flight-finder-reset"
                            >
                                Start again
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


/* -------------------------------------------------------------------------- */
/* FAQ + Satisfaction Guarantee                                               */
/*                                                                            */
/* A dedicated section under Flight Packages that answers the questions       */
/* clients most often ask before hitting "Book" — reduces hesitation and      */
/* pairs with the Satisfaction Promise badge above the terms row.             */
/* -------------------------------------------------------------------------- */

const FAQ_ITEMS = [
    {
        q: "What happens if I don't like the footage?",
        a: "I offer a satisfaction guarantee. We will discuss your vision in detail before the shoot, and I provide a review phase for all edits to ensure the final result is exactly what you need.",
    },
    {
        q: "How does the review process work?",
        a: "After the shoot, you receive a first cut for review within 7 days. You send me your notes — timings, colour, pacing, music, anything — and I revise until it feels right. Minor tweaks are always included.",
    },
    {
        q: "What if the weather is bad on my chosen date?",
        a: "We reschedule at no cost. Scottish weather has opinions of its own, and pushing through a bad ceiling is how you end up with grey, flat footage. I'd rather move a day than deliver something forgettable.",
    },
    {
        q: "Do I own the final footage?",
        a: "Yes. You receive a full commercial licence for the delivered edits and raw files, in perpetuity. I retain the right to use small excerpts in my portfolio unless you'd prefer otherwise (just say the word).",
    },
    {
        q: "How far in advance should I book?",
        a: "Aim for 2–4 weeks where possible — golden-hour and event slots go quickly. Same-week bookings are sometimes doable; email me and we'll see.",
    },
    {
        q: "Are you insured and CAA-registered?",
        a: "Yes to both. Every flight includes a full CAA pre-flight safety assessment and risk report, and I carry full public liability insurance up to £5m.",
    },
];

function FAQItem({ q, a, index }) {
    const [open, setOpen] = useState(index === 0);
    return (
        <div
            className="border-b border-[var(--sb-border)]"
            data-testid={`booking-faq-item-${index}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full text-left py-5 flex items-start justify-between gap-4 group"
                aria-expanded={open}
                data-testid={`booking-faq-toggle-${index}`}
            >
                <span className="font-serif text-lg md:text-xl leading-snug text-[var(--sb-text)] group-hover:text-white transition-colors">
                    {q}
                </span>
                <span
                    aria-hidden
                    className={`shrink-0 mt-1 w-6 h-6 flex items-center justify-center border transition-transform duration-300 ${open ? "rotate-45" : ""}`}
                    style={{ borderColor: "#0055A4", color: "#5A9BD8" }}
                >
                    <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                        <line x1="6" y1="1" x2="6" y2="11" />
                        <line x1="1" y1="6" x2="11" y2="6" />
                    </svg>
                </span>
            </button>
            <div
                className="grid overflow-hidden transition-all duration-300 ease-out"
                style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
            >
                <div className="min-h-0">
                    <p className="text-sm md:text-base text-[var(--sb-text-dim)] leading-relaxed pb-5 pr-10">
                        {a}
                    </p>
                </div>
            </div>
        </div>
    );
}

function BookingFAQ() {
    return (
        <div className="mt-20" data-testid="booking-faq">
            <div className="text-center mb-10">
                <div className="overline mb-3" style={{ color: "#5A9BD8", fontWeight: 700 }}>
                    Before you book
                </div>
                <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight" style={{ color: "var(--sb-text)" }}>
                    Questions? Here&rsquo;s the honest answer.
                </h3>
            </div>

            <div className="max-w-3xl mx-auto">
                {FAQ_ITEMS.map((item, i) => (
                    <FAQItem key={i} q={item.q} a={item.a} index={i} />
                ))}
            </div>

            {/* Satisfaction Guarantee — the closing reassurance ------------- */}
            <div
                className="max-w-3xl mx-auto mt-12 p-8 md:p-10 text-center relative"
                style={{
                    background: "linear-gradient(135deg, rgba(0,85,164,0.14) 0%, rgba(20,42,77,0.45) 100%)",
                    border: "1px solid rgba(107,167,222,0.35)",
                }}
                data-testid="satisfaction-guarantee"
            >
                <span aria-hidden className="absolute top-0 left-0 w-4 h-4 border-t border-l" style={{ borderColor: "#0055A4" }} />
                <span aria-hidden className="absolute top-0 right-0 w-4 h-4 border-t border-r" style={{ borderColor: "#0055A4" }} />
                <span aria-hidden className="absolute bottom-0 left-0 w-4 h-4 border-b border-l" style={{ borderColor: "#0055A4" }} />
                <span aria-hidden className="absolute bottom-0 right-0 w-4 h-4 border-b border-r" style={{ borderColor: "#0055A4" }} />

                <div className="overline mb-4" style={{ color: "#5A9BD8", fontWeight: 700 }}>
                    Satisfaction Guarantee
                </div>
                <p
                    className="font-serif text-xl md:text-2xl leading-snug"
                    style={{ color: "var(--sb-text)" }}
                    data-testid="satisfaction-guarantee-body"
                >
                    Your satisfaction is my top priority. I believe in a collaborative process,
                    which means we won&rsquo;t stop until you are&nbsp;100% happy with the final result.
                </p>
            </div>
        </div>
    );
}

